import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanActivateChild,
    CanMatch,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree
} from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoginService } from '../services/login.service';
import { AuthStateService } from '../services/auth_state.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild, CanMatch {
    constructor(
        private loginService: LoginService,
        private router: Router,
        private authState: AuthStateService
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean | UrlTree | Observable<boolean | UrlTree> {
        return this.evaluate(state.url);
    }

    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean | UrlTree | Observable<boolean | UrlTree> {
        return this.evaluate(state.url);
    }

    canMatch(
        route: Route,
        segments: UrlSegment[]
    ): boolean | UrlTree | Observable<boolean | UrlTree> {
        const currentNavigationUrl = this.resolveCurrentNavigationUrl();
        if (currentNavigationUrl) {
            return this.evaluate(currentNavigationUrl);
        }

        const url = '/' + segments.map((segment) => segment.path).join('/');
        return this.evaluate(url);
    }

    private evaluate(url: string): boolean | UrlTree | Observable<boolean | UrlTree> {
        if (this.loginService.isSessionValid()) {
            return true;
        }

        if (this.loginService.isBypassLoginEnabled()) {
            return this.loginService.ensureBypassLogin().pipe(
                map((success) => {
                    if (success) {
                        return true;
                    }
                    this.authState.captureRequestedUrl(url);
                    return this.router.parseUrl('/authentication/login');
                })
            );
        }

        this.authState.captureRequestedUrl(url);
        return this.router.parseUrl('/authentication/login');
    }

    private resolveCurrentNavigationUrl(): string | null {
        const currentNavigation = this.router.getCurrentNavigation();
        const navigationUrl = currentNavigation?.initialUrl ?? currentNavigation?.extractedUrl;
        if (!navigationUrl) {
            return null;
        }

        return this.router.serializeUrl(navigationUrl);
    }
}
